/**
 * Comentários ao vivo (P-32; §8 de 14/10, noite; proposta do front, seção 2): a vaga do painel na coluna direita sem
 * foco (acima de "Últimas atualizações", no lugar do quadro de situação incomum) e na seção "Mais sobre a apuração" do
 * celular (panelsSlot.tsx). Só monta com `features.commentary` (ligada na publicação de até 23/10, com a confirmação do
 * go/no-go), com a camada no snapshot (`files.commentary`), a partir da abertura das urnas e nunca na TV.
 *
 * Chunk `commentary` (DE-C20): o painel, o catálogo e o validador ficam fora do JS crítico.
 */
import { ErrorBoundary } from '../features/errors/ErrorBoundary';
import { useConfig, useHasFocus, useLayout, usePhase } from '../state/hooks';
import { commentaryPath, commentaryStatus } from '../state/store';
import { isWide } from './layoutMode';
import { lazyChunk, useLazyChunk } from './lazyChunk';

export const commentaryChunk = lazyChunk(() => import('./commentaryLive'));

function CommentaryChunk() {
  const mod = useLazyChunk(commentaryChunk, true);
  if (!mod) return null;
  const { LiveCommentary } = mod;
  return <LiveCommentary />;
}

/** O painel de comentários monta agora (as mesmas condições do slot): a coluna direita usa para as abas (proposta, 2.1). */
export function useCommentaryOn(): boolean {
  const cfg = useConfig();
  const path = commentaryPath.value;
  const phase = usePhase();
  const layout = useLayout();
  return cfg.features.commentary && path !== null && phase !== 'pre_open' && layout.mode !== 'tv';
}

/**
 * Os comentários aparecem (as abas da coluna direita, o painel): a camada montada e o índice fora de `off`. Antes de o
 * índice chegar (null), como antes; com `off`, o site fica como sem comentários (pré-mortem de 25/10, P-87).
 */
export function useCommentaryShown(): boolean {
  const on = useCommentaryOn();
  return on && commentaryStatus.value !== 'off';
}

/** O sensor do índice (sem tela), montado pela casca enquanto a camada existe, com ou sem o painel (P-87). */
export function CommentaryProbeSlot() {
  if (!useCommentaryOn()) return null;
  return (
    <ErrorBoundary name="commentary-probe" quiet>
      <CommentaryProbe />
    </ErrorBoundary>
  );
}

function CommentaryProbe() {
  const mod = useLazyChunk(commentaryChunk, true);
  if (!mod) return null;
  const { CommentaryStatusProbe } = mod;
  return <CommentaryStatusProbe />;
}

export function CommentarySlot() {
  if (!useCommentaryShown()) return null;
  return (
    <ErrorBoundary name="commentary">
      <CommentaryChunk />
    </ErrorBoundary>
  );
}

/**
 * Computador estreito (1000 a 1279 px de palco, sem foco): não há coluna direita, e o painel entra na esquerda, logo
 * abaixo do cartão principal (ORD-F-022, item 1; medido na prévia em 1060 px, onde o painel não existia).
 */
export function useNarrowDesktop(): boolean {
  const layout = useLayout();
  const hasFocus = useHasFocus();
  return layout.mode === 'desktop' && !hasFocus && !isWide(layout);
}

export function NarrowCommentarySlot() {
  if (!useNarrowDesktop()) return null;
  return <CommentarySlot />;
}

/**
 * Tablet e celular deitado, sem foco: não há coluna direita nem a seção "Mais", e o painel entra no cartão principal,
 * logo abaixo do herói (o cartão rola por dentro). O celular em pé segue com o painel em "Mais" (ORD-F-022).
 * Com a tela "Linha do tempo" no ar (`commentary_timeline`), o painel sai do cartão: os comentários ficam na troca, que
 * ocupa a área principal, e o mapa do tablet não é espremido pelo cartão (opção (a) do AVISO-F-056; §8 de 17/10 ~19h35).
 */
export function useCommentaryInCard(): boolean {
  const layout = useLayout();
  const hasFocus = useHasFocus();
  const cfg = useConfig();
  return (layout.mode === 'tablet' || layout.mode === 'land') && !hasFocus && !cfg.features.commentary_timeline;
}

export function CardCommentarySlot() {
  if (!useCommentaryInCard()) return null;
  return <CommentarySlot />;
}
